/**
 * AlternativasDoLaudo — as opções do catálogo para liberar o disco do alerta
 * (spec 2026-09-27 §5, §11.15, D5).
 *
 * O botão "Escolher" não executa nada: ele pede ao servidor o dry-run da
 * alternativa, e o plano chega como proposta nova na conversa, com o cartão de
 * confirmação de sempre (portões, plano, GMUD em produção). Por isso aqui pode
 * haver botão, e no bloco "O que está ocupando o disco" do laudo não.
 *
 * Três regras que vêm da spec, não do desenho livre:
 * 1. **Sem campo de texto.** O comando próprio está congelado (D5); só o catálogo.
 * 2. **Ganho não medido não vira número.** `teto_bytes: null` diz "ganho não
 *    medido"; o teto medido é "até", nunca uma promessa.
 * 3. **A máquina vem do metadata** (nome e id do laudo), nunca do texto do bot,
 *    que redige o id (§11.15).
 */
import { Anchor, Badge, Button, Code, Group, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { IconAlertTriangle, IconRepeat, IconTrash } from '@tabler/icons-react'
import type { ReactElement } from 'react'

import { useEscolherAlternativa } from '../../../hooks/useChat'
import { formatarBytes } from './bytes'

export interface Alternativa {
  verbo: string
  nome: string
  comando: string
  params: Record<string, string>
  caminho: string
  teto_bytes: number | null
  teto_pp: number | null
  risco_ordem: number
  risco_resumo: string
  ativo: boolean
  recomendada: boolean
  motivo_recomendacao: string | null
  basta_sozinha: boolean | null
}

interface Props {
  conversationId: number
  messageId: number
  alternativas: Alternativa[]
  motivoSemAlternativa: string | null
  maquina: { nome: string; instanceId: string | null }
  isProd: boolean
  /** Montagem do alerta (`laudo.montagem_do_alerta.montagem`), quando o laudo a traz. */
  montagem?: string | null
  /** Laudo preparado por uma regra de ordem (`metadata.regra_de_ordem`, spec 2026-09-30
   * §5.2): a marca no topo e "fora da regra" nas alternativas fora de `verbos`. */
  regra?: RegraDoLaudo | null
}

export interface RegraDoLaudo {
  regraId: number
  verbos: string[]
  /** Nome do dono, gravado no preparo; laudo antigo não tem — a marca mostra só o número. */
  donoNome?: string | null
  /** Tenant da regra; com ele, a marca leva à lista de regras do tenant. */
  tenantId?: number | null
}

/** `metadata.regra_de_ordem` → `RegraDoLaudo`, ou null. Defensivo: metadata é JSON livre,
 * e sem `regra_id` numérico não há marca a afirmar. */
export function lerRegraDeOrdem(bruto: unknown): RegraDoLaudo | null {
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return null
  const r = bruto as Record<string, unknown>
  if (typeof r.regra_id !== 'number') return null
  const verbos = Array.isArray(r.verbos_da_regra)
    ? r.verbos_da_regra.filter((v): v is string => typeof v === 'string')
    : []
  return {
    regraId: r.regra_id,
    verbos,
    donoNome: typeof r.dono_nome === 'string' && r.dono_nome ? r.dono_nome : null,
    tenantId: typeof r.tenant_id === 'number' ? r.tenant_id : null,
  }
}

const MONO = '"JetBrains Mono", monospace'

function ganho(a: Alternativa): string {
  if (a.teto_bytes == null) return 'ganho não medido'
  const pp = a.teto_pp != null
    ? ` (≈ ${a.teto_pp.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} pp)`
    : ''
  return `até ${formatarBytes(a.teto_bytes)}${pp}`
}

export function AlternativasDoLaudo({
  conversationId,
  messageId,
  alternativas,
  motivoSemAlternativa,
  maquina,
  isProd,
  montagem,
  regra = null,
}: Props): ReactElement {
  const escolher = useEscolherAlternativa()
  const escolhendo = escolher.isPending ? escolher.variables?.verbo : undefined

  return (
    <Stack gap={6} mt="sm" data-alternativas>
      {regra && (
        <Group gap={6} wrap="nowrap">
          <IconRepeat size={16} style={{ flexShrink: 0 }} />
          <Text size="xs">
            Preparado pela regra{' '}
            {regra.tenantId != null ? (
              // A marca é o caminho até a lista: a página não está no menu nesta fatia (§7).
              <Anchor component={Link} to={`/tenants/${regra.tenantId}/regras-de-ordem`}
                      size="xs" fw={600} style={{ fontFamily: MONO }}>
                #{regra.regraId}
              </Anchor>
            ) : (
              <Text span size="xs" fw={600} style={{ fontFamily: MONO }}>#{regra.regraId}</Text>
            )}
            {regra.donoNome ? ` de ${regra.donoNome}` : null}
          </Text>
        </Group>
      )}
      <Text size="xs" fw={600} c="dimmed">
        {montagem ? (
          <>
            Alternativas para liberar espaço em{' '}
            <Text span size="xs" fw={600} style={{ fontFamily: MONO }}>{montagem}</Text>
          </>
        ) : 'Alternativas para liberar espaço'}
      </Text>
      <Group gap={6} wrap="wrap">
        <Text size="xs">{maquina.nome}</Text>
        {maquina.instanceId && (
          <Text size="xs" style={{ fontFamily: MONO, color: 'var(--ai-text-secondary)' }}>
            {maquina.instanceId}
          </Text>
        )}
      </Group>

      {alternativas.length === 0 ? (
        <Stack gap={2}>
          {motivoSemAlternativa && <Text size="xs">{motivoSemAlternativa}</Text>}
          <Text size="xs" c="dimmed">
            Não há limpeza do catálogo para este caso. O caminho é abrir um chamado para o
            time DevOps.
          </Text>
        </Stack>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {alternativas.map((a, i) => (
            <li
              key={a.verbo}
              style={{
                padding: '8px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--ai-border-light)',
              }}
            >
              <Stack gap={4}>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <IconTrash size={16} style={{ marginTop: 1, flexShrink: 0 }} />
                  <Text size="sm" fw={600}>{a.nome}</Text>
                  {i === 0 && a.recomendada && (
                    <Badge size="xs" variant="light" color="inovareTeal">Recomendada</Badge>
                  )}
                  {regra && !regra.verbos.includes(a.verbo) && (
                    <Badge size="xs" variant="outline" color="gray">fora da regra</Badge>
                  )}
                </Group>
                {i === 0 && a.recomendada && a.motivo_recomendacao && (
                  <Text size="xs">{a.motivo_recomendacao}</Text>
                )}
                <Code style={{ fontFamily: MONO, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {a.comando}
                </Code>
                <Text size="xs" style={{ fontFamily: a.teto_bytes == null ? undefined : MONO }}>
                  {ganho(a)}
                </Text>
                {a.risco_resumo && <Text size="xs" c="dimmed">{a.risco_resumo}</Text>}
                {a.ativo ? (
                  <Group gap="xs">
                    <Button
                      size="xs"
                      color="inovareTeal"
                      variant={i === 0 && a.recomendada ? 'filled' : 'light'}
                      loading={escolhendo === a.verbo}
                      disabled={escolher.isPending && escolhendo !== a.verbo}
                      onClick={() =>
                        escolher.mutate({ cid: conversationId, mid: messageId, verbo: a.verbo })}
                    >
                      Escolher
                    </Button>
                    {isProd && (
                      <Text size="xs" c="dimmed">
                        Em produção, outro integrante do time DevOps aprova antes de executar.
                      </Text>
                    )}
                  </Group>
                ) : (
                  <Group gap={4} wrap="nowrap">
                    <IconAlertTriangle size={16} color="var(--ai-text-secondary)" />
                    <Text size="xs" c="dimmed">desligada neste ambiente</Text>
                  </Group>
                )}
              </Stack>
            </li>
          ))}
        </ul>
      )}
    </Stack>
  )
}
